import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import {
  WorkspaceDocumentIcon,
  WorkspaceFolderCloseIcon,
  WorkspaceFolderOpenIcon,
  WorkspaceFormIcon
} from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DealFolderForm, IFile, IFolder } from "store/types";
import { Document } from "store/types/documents";

interface Props {
  folder: IFolder;
  allFolders: IFolder[];
  files: IFile[];
  forms: DealFolderForm[];
  documents: Document[];
  isFolderSelected: (id: number) => boolean;
  isFileSelected: (id: number) => boolean;
  isFormSelected: (id: number) => boolean;
  isDocumentSelected: (id: number) => boolean;
  onToggleFolder: (id: number) => void;
  onToggleFile: (item: { id: number; parentId: number | null }) => void;
  onToggleForm: (item: { id: number; parentId: number | null }) => void;
  onToggleDocument: (item: { id: number; parentId: number | null }) => void;
  level?: number;
}

export const TemplateContentFolder: React.FC<Props> = ({
  folder,
  allFolders,
  files,
  forms,
  documents,
  isFolderSelected,
  isFileSelected,
  isFormSelected,
  isDocumentSelected,
  onToggleFolder,
  onToggleFile,
  onToggleForm,
  onToggleDocument,
  level = 0
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  const childFolders = allFolders.filter((f) => f.parent_id === folder.id);
  const folderFiles = files.filter((f) => f.section_id === folder.id);
  const folderForms = forms.filter((f) => f.section_id === folder.id);
  const folderDocuments = documents.filter((d) => d.section_id === folder.id);
  const hasChildren =
    childFolders.length > 0 || folderFiles.length > 0 || folderForms.length > 0 || folderDocuments.length > 0;
  const checked = isFolderSelected(folder.id);

  return (
    <Accordion
      expanded={expanded}
      sx={{
        boxShadow: "none",
        "&.Mui-expanded": { margin: 0 }
      }}
    >
      <AccordionSummary
        sx={{
          overflow: "hidden",
          padding: 0,
          height: 39,
          minHeight: "39px !important",
          maxHeight: "39px !important",
          paddingLeft: `${level * 24}px`,
          "&:hover": { backgroundColor: "#f5f9fa" },
          "&.Mui-expanded": {
            height: "39px !important",
            minHeight: "39px !important",
            maxHeight: "39px !important"
          },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            overflow: "hidden",
            "&.Mui-expanded": { margin: 0 }
          }
        }}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest(".expand-icon")) return;
          if ((e.target as HTMLElement).closest(".folder-checkbox")) return;
          if (hasChildren) {
            setExpanded(!expanded);
          }
        }}
      >
        <Stack sx={{ width: "100%", flexDirection: "row", alignItems: "center", gap: "4px", overflow: "hidden" }}>
          <Checkbox
            className="folder-checkbox"
            checked={checked}
            onChange={() => onToggleFolder(folder.id)}
            size="small"
            sx={{ padding: "4px" }}
          />
          <Stack sx={{ flexDirection: "row", alignItems: "center" }}>
            {hasChildren ? (
              <IconButton className="expand-icon" onClick={() => setExpanded(!expanded)}>
                <ExpandMoreIcon
                  sx={{ fill: "#0A3142", fontSize: "20px", transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}
                />
              </IconButton>
            ) : (
              <Box sx={{ width: 28, height: 28 }} />
            )}
            {expanded && hasChildren ? (
              <WorkspaceFolderOpenIcon sx={{ fill: "#0A3142", fontSize: "20px" }} />
            ) : (
              <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142", fontSize: "20px" }} />
            )}
          </Stack>
          <MaterialTooltip
            title={folder.title}
            isShow
            variant="dark"
            disableHoverListener={!folder.title || folder.title.length <= 45}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#0a3142",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                flex: 1
              }}
            >
              {folder.title}
            </Typography>
          </MaterialTooltip>
        </Stack>
      </AccordionSummary>
      {hasChildren && (
        <AccordionDetails sx={{ padding: 0 }}>
          {folderFiles.map((file) => (
            <Stack
              key={`file-${file.id}`}
              direction="row"
              alignItems="center"
              gap="4px"
              sx={{
                paddingLeft: `${(level + 1) * 24 + 28}px`,
                height: 36,
                "&:hover": { backgroundColor: "#f5f9fa" }
              }}
            >
              <Checkbox
                checked={isFileSelected(file.id)}
                onChange={() => onToggleFile({ id: file.id, parentId: folder.id })}
                size="small"
                sx={{ padding: "4px" }}
              />
              <InsertDriveFileOutlinedIcon sx={{ fill: "#5d7782", fontSize: "18px" }} />
              <MaterialTooltip
                title={file.name}
                isShow
                variant="dark"
                disableHoverListener={!file.name || file.name.length <= 45}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#0a3142",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden"
                  }}
                >
                  {file.name}
                </Typography>
              </MaterialTooltip>
            </Stack>
          ))}
          {folderForms.map((form) => (
            <Stack
              key={`form-${form.id}`}
              direction="row"
              alignItems="center"
              gap="4px"
              sx={{
                paddingLeft: `${(level + 1) * 24 + 28}px`,
                height: 36,
                "&:hover": { backgroundColor: "#f5f9fa" }
              }}
            >
              <Checkbox
                checked={isFormSelected(form.id)}
                onChange={() => onToggleForm({ id: form.id, parentId: folder.id })}
                size="small"
                sx={{ padding: "4px" }}
              />
              <WorkspaceFormIcon sx={{ fill: "#5d7782", fontSize: "18px" }} />
              <MaterialTooltip
                title={form.title}
                isShow
                variant="dark"
                disableHoverListener={!form.title || form.title.length <= 45}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#0a3142",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden"
                  }}
                >
                  {form.title}
                </Typography>
              </MaterialTooltip>
            </Stack>
          ))}
          {folderDocuments.map((doc) => (
            <Stack
              key={`doc-${doc.id}`}
              direction="row"
              alignItems="center"
              gap="4px"
              sx={{
                paddingLeft: `${(level + 1) * 24 + 28}px`,
                height: 36,
                "&:hover": { backgroundColor: "#f5f9fa" }
              }}
            >
              <Checkbox
                checked={isDocumentSelected(doc.id)}
                onChange={() => onToggleDocument({ id: doc.id, parentId: folder.id })}
                size="small"
                sx={{ padding: "4px" }}
              />
              <WorkspaceDocumentIcon sx={{ fill: "#5d7782", fontSize: "18px" }} />
              <MaterialTooltip
                title={doc.title}
                isShow
                variant="dark"
                disableHoverListener={!doc.title || doc.title.length <= 45}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#0a3142",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden"
                  }}
                >
                  {doc.title}
                </Typography>
              </MaterialTooltip>
            </Stack>
          ))}
          {childFolders.map((childFolder) => (
            <TemplateContentFolder
              key={childFolder.id}
              folder={childFolder}
              allFolders={allFolders}
              files={files}
              forms={forms}
              documents={documents}
              isFolderSelected={isFolderSelected}
              isFileSelected={isFileSelected}
              isFormSelected={isFormSelected}
              isDocumentSelected={isDocumentSelected}
              onToggleFolder={onToggleFolder}
              onToggleFile={onToggleFile}
              onToggleForm={onToggleForm}
              onToggleDocument={onToggleDocument}
              level={level + 1}
            />
          ))}
        </AccordionDetails>
      )}
    </Accordion>
  );
};
